/**
 * AI forecast for a v2 model.
 *
 * Running the forecast and APPLYING it are deliberately separate. Applying
 * overwrites input figures the user may have entered by hand, so it is a
 * decision they make after seeing the numbers, not a side effect of asking for
 * them. The distinction is also what makes the forecast safe to re-run.
 *
 * ACCOUNTING ONLY. This view used to show the enterprise value range and what moves
 * it, from the same pipeline run, while sitting in the Valuation tab group - so one
 * tab did two disciplines' work and the accounting half was somewhere an accounting
 * user could not reach. The valuation half is now `valuation/AiValuationViewV2.tsx`,
 * reading the same persisted distribution. The pipeline runs once.
 *
 * What is left is forecasting: what each driver is expected to do over the years
 * asked for, how confident the fit is, and writing those figures into the inputs.
 */

import { useState } from 'react';
import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { Markdown } from '../Markdown';
import { CollapsibleNotes } from '../CollapsibleNotes';
import { formatMoney } from '../../../lib/currency';
import { formatPosterior, groupPosteriors } from './posteriors';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';

/**
 * What the Bayesian layer concluded, per driver.
 *
 * The enterprise value range above is the OUTPUT of the simulation. This is
 * what went into it: the posterior for each driver the forecasters fitted,
 * with its 10th–90th percentile. Both had always been computed and only the
 * first was ever shown, so the valuation looked uncertain for no visible
 * reason.
 *
 * The interval is drawn as a bar on a scale shared by the whole group, so a
 * year the model is less sure about is visibly wider rather than merely
 * numerically wider. The figures are there too — the bar is for comparison,
 * the numbers are for reading.
 */
export function PosteriorPanel({
  summaries,
  currency,
}: {
  summaries: Record<string, Record<string, number>> | undefined;
  currency: string;
}) {
  const groups = groupPosteriors(summaries);
  if (groups.length === 0) return null;

  const fmt = (value: number, unit: 'percent' | 'money') =>
    formatPosterior(value, unit, (v) => formatMoney(v, currency));

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-2.5">
        <div className="text-sm font-semibold text-gray-700">
          What the model believes about each driver
        </div>
        <div className="mt-0.5 text-xs text-gray-600">
          Posterior distributions from the Bayesian fit, shown as the median
          and the 10th–90th percentile. These are the inputs the simulation
          drew from — the enterprise value range above is what came out.
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {groups.map((group) => (
          <div key={group.key} className="px-4 py-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-gray-900">
                {group.title}
              </span>
              <span className="text-xs uppercase tracking-wide text-gray-400">
                {group.forecaster}
              </span>
            </div>

            <table className="mt-2 min-w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th scope="col" className="w-16 py-1 text-left font-medium">
                    {group.rows.some((r) => r.label) ? 'Year' : ''}
                  </th>
                  <th scope="col" className="py-1 text-right font-medium">P10</th>
                  <th scope="col" className="py-1 text-right font-medium">Median</th>
                  <th scope="col" className="py-1 text-right font-medium">P90</th>
                  <th scope="col" className="w-2/5 py-1 pl-4 text-left font-medium">
                    Credible interval
                  </th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={`${group.key}-${row.label}`}>
                    <td className="py-1 text-gray-600">{row.label}</td>
                    <td className="py-1 text-right tabular-nums text-gray-500">
                      {fmt(row.p10, group.unit)}
                    </td>
                    <td className="py-1 text-right font-medium tabular-nums text-gray-900">
                      {fmt(row.median, group.unit)}
                    </td>
                    <td className="py-1 text-right tabular-nums text-gray-500">
                      {fmt(row.p90, group.unit)}
                    </td>
                    <td className="py-1 pl-4">
                      <div
                        className="relative h-2 rounded bg-gray-100"
                        title={`${fmt(row.p10, group.unit)} to ${fmt(row.p90, group.unit)} from ${row.n.toLocaleString()} samples`}
                      >
                        <div
                          className="absolute h-2 rounded bg-blue-200"
                          style={{
                            left: `${row.barStart}%`,
                            width: `${Math.max(row.barEnd - row.barStart, 1)}%`,
                          }}
                        />
                        <div
                          className="absolute h-2 w-0.5 bg-blue-700"
                          style={{ left: `${row.barMedian}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ForecastViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const forecast = useFinancialModelV2Store((s) => s.forecast);
  const isForecasting = useFinancialModelV2Store((s) => s.isForecasting);
  const forecastProgress = useFinancialModelV2Store((s) => s.forecastProgress);
  const error = useFinancialModelV2Store((s) => s.forecastError);
  const runForecast = useFinancialModelV2Store((s) => s.runForecast);
  const applyForecast = useFinancialModelV2Store((s) => s.applyForecast);

  const [confirmingApply, setConfirmingApply] = useState(false);
  /** Set by the one deliberate click that forecasts from accounts that do not add up. */
  const [forecastAnyway, setForecastAnyway] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  if (!model) return null;

  const currency = model.settings.baseCurrency;
  // The years this forecast covers, which is what an accounting user came for.
  // `forecast.enterpriseValue` is deliberately not read here - see AI Valuation.
  //
  // The model's SPAN LESS ITS HISTORY. `numForecastYears` is the whole span including the
  // filed years, so taking it whole named the actuals as the thing being forecast: a PDF
  // import now spans exactly the years the filing reports - nothing is invented past them -
  // and this tab offered to forecast 2023 and 2024 of a filing that reports 2023 and 2024.
  const historicalYears = new Set(model.settings.historicalYears ?? []);
  const forecastYears = Array.from(
    { length: model.settings.numForecastYears },
    (_, index) => model.settings.forecastStartYear + index,
  ).filter((year) => !historicalYears.has(year));

  // STEP THREE of the import flow: import what was read, correct the figures the filing
  // disagrees with, then forecast. Every driver here is measured from the model's own
  // history, so history that does not add up gives drivers measured from something wrong -
  // and a forecast looks authoritative whatever it was fitted to.
  //
  // Held back rather than refused. Refusing outright is the mistake step one corrected, so
  // the button waits behind one deliberate click and the backend puts the same fact in the
  // forecast's own warnings, where no front end can lose it.
  const unbalanced = Object.entries(
    model.computed.balanceSheet.balanceCheck ?? {},
  ).filter(([, value]) => Math.abs(value) > 1);
  const worstUnbalanced = unbalanced.reduce<[string, number] | null>(
    (worst, entry) => (!worst || Math.abs(entry[1]) > Math.abs(worst[1]) ? entry : worst),
    null,
  );
  const blocked = unbalanced.length > 0 && !forecastAnyway;

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-1 text-lg font-bold text-gray-800">AI forecast</h2>
      <p className="mb-4 max-w-3xl text-sm text-gray-500">
        Forecasts the accounting lines for the years this model is set up for,
        from its own history, using sector benchmarks and peer data. What the
        result is WORTH is a valuation question — see Valuation &rsaquo; AI
        Valuation, which reads the same run.
      </p>

      {/* A model of pure history has nothing to forecast INTO, which is the normal state of
          a fresh PDF import: the import reads the years the filing reports and invents none
          past them. Saying so beats a run that returns nothing, and it names the one place
          that changes it. */}
      {forecastYears.length === 0 && (
        <div className="mb-5 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          This model covers{' '}
          {model.settings.historicalYears?.length
            ? `${model.settings.historicalYears[0]}–${
                model.settings.historicalYears[model.settings.historicalYears.length - 1]
              }`
            : 'its filed years'}{' '}
          and no years beyond them, so there is nothing to forecast yet. An import reads
          what the filing reports and adds no years of its own. Add the years you want in
          Settings, under the number of years this model covers, and they will appear here.
        </div>
      )}

      {/* The sheet has to add up before a forecast means anything. Named with the figure and
          the period, and pointing at the tab that says which figure the filing's own text
          disagrees with, rather than telling the user to go and find out. */}
      {unbalanced.length > 0 && worstUnbalanced && (
        <div className="mb-5 max-w-3xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          <div className="font-semibold">
            The accounts do not balance yet — {worstUnbalanced[0]} is out by{' '}
            {worstUnbalanced[1].toLocaleString('en-US', { maximumFractionDigits: 0 })}.
          </div>
          <p className="mt-1">
            Every driver in a forecast is measured from this model's own history, so history
            that does not add up gives drivers measured from a figure that is wrong. The
            Overview tab names which figure the filing's own text disagrees with. Correct
            that first and this runs on accounts that hold together.
          </p>
          {!forecastAnyway && (
            <button
              type="button"
              onClick={() => setForecastAnyway(true)}
              className="mt-2 text-sm text-red-800 underline hover:no-underline"
            >
              Forecast anyway
            </button>
          )}
          {forecastAnyway && (
            <p className="mt-2 text-xs">
              Forecasting from unbalanced accounts. The result will carry this as its first
              note.
            </p>
          )}
        </div>
      )}

      <div className="mb-5 flex gap-2">
        <button
          type="button"
          disabled={isForecasting || forecastYears.length === 0 || blocked}
          onClick={() => void runForecast(false)}
          className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {isForecasting ? 'Forecasting…' : forecast ? 'Run again' : 'Run forecast'}
        </button>
        {forecast && (
          <button
            type="button"
            disabled={isForecasting}
            onClick={() => void runForecast(true)}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Regenerate (ignore cache)
          </button>
        )}
      </div>

      {isForecasting && (
        <div className="mb-4 max-w-3xl rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <WorkingIndicator
            label="Running four forecasters and a Monte Carlo simulation"
            detail={forecastProgress || undefined}
          />
          <p className="mt-1.5 text-xs text-blue-800">
            This takes a minute or two the first time; repeat runs are cached.
            It keeps going if you move to another tab.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 max-w-3xl rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {forecast && (
        <div className="max-w-4xl space-y-4">
          {/* What this forecast actually produced, for the accounting half: figures
              for the years asked for. The enterprise value the same run computes is
              a valuation question and is shown under Valuation > AI Valuation - one
              pipeline, two views, each showing its own half. */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Forecast years
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {forecastYears.length > 0
                ? `${forecastYears[0]}–${forecastYears[forecastYears.length - 1]}`
                : '—'}
            </div>
            <div className="mt-1 text-sm text-gray-600">
              {forecastYears.length} year
              {forecastYears.length === 1 ? '' : 's'} of revenue, staff costs,
              overheads, capex and depreciation, fitted from this model's own
              history.
            </div>
            <div className="mt-2 text-xs text-gray-500">
              From {forecast.nSamples.toLocaleString()} simulations
              {forecast.cacheHit && ' · served from cache'}
            </div>
          </div>

          <CollapsibleNotes
            title="Points to check"
            notes={forecast.forecasterWarnings}
          />

          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Apply to the model
                </div>
                <div className="mt-0.5 text-xs text-gray-600">
                  Writes the forecast figures into the input tabs. Saved to
                  history first, so it can be undone from Versions.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConfirmingApply(true)}
                className="shrink-0 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Apply
              </button>
            </div>
          </div>

          <PosteriorPanel
            summaries={forecast.posteriorSummaries}
            currency={currency}
          />

          <div className="rounded-lg border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => setShowMethodology((open) => !open)}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left"
            >
              <span className="text-sm font-semibold text-gray-700">
                How this was produced
              </span>
              <span className="text-xs text-gray-500">
                {showMethodology ? 'Hide' : 'Show'}
              </span>
            </button>
            {showMethodology && (
              /* The methodology comes back as markdown - headings, lists and
                 tables - and was being printed as its own source, hashes and
                 pipes and all. Rendered with the same library and prose sizing
                 the chat panel uses, so an explanation reads the same wherever
                 it appears.

                 Typography bakes its own ink as literals at build time, so it
                 does not follow the themed ramp on its own; globals.css
                 re-points the --tw-prose-* variables for exactly that. */
              <div className="border-t border-gray-200 px-4 py-3">
                <Markdown>{forecast.methodology}</Markdown>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmingApply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-900">
              Apply the forecast?
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              This overwrites the forecast years on the Revenue, Indirect Costs,
              Staffing and Fixed Assets tabs. The model as it stands now is
              saved to history first, so you can come back to it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingApply(false)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setConfirmingApply(false);
                  await applyForecast();
                }}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
